import React, { useEffect, useRef, useState } from "react";
import * as tus from "tus-js-client";
import { api, formatApiError } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, CheckCircle, Loader2, AlertTriangle } from "lucide-react";

/**
 * Direct-to-Vimeo TUS uploader.
 * 1. Backend creates an upload ticket → returns upload_link + vimeo URI
 * 2. Browser streams the file directly to Vimeo (server bandwidth = zero)
 * 3. Backend finalizes: privacy=disable, whitelist our domains
 * 4. onUploaded(vimeo_video_id) called with the result
 */
export default function VimeoUploader({ value, onUploaded, onClear }) {
  const [configured, setConfigured] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pct, setPct] = useState(0);
  const [status, setStatus] = useState("");
  const fileRef = useRef(null);
  const uploadRef = useRef(null);

  useEffect(() => {
    api.get("/admin/vimeo/status")
      .then(r => setConfigured(r.data.configured))
      .catch(() => setConfigured(false));
  }, []);

  const startUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please pick a video file");
      return;
    }
    setUploading(true);
    setPct(0);
    setStatus("Creating Vimeo upload ticket…");

    let ticket;
    try {
      const { data } = await api.post("/admin/vimeo/ticket", {
        file_size: file.size, file_name: file.name,
      });
      ticket = data;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Vimeo ticket failed");
      setUploading(false);
      return;
    }

    setStatus("Uploading directly to Vimeo…");
    const upload = new tus.Upload(file, {
      uploadUrl: ticket.upload_link,
      endpoint: ticket.upload_link,
      retryDelays: [0, 1000, 3000, 5000, 10000, 20000],
      chunkSize: 128 * 1024 * 1024,
      metadata: { filename: file.name, filetype: file.type },
      onError: (err) => {
        toast.error(`Upload failed: ${err.message || err}`);
        setUploading(false);
        setPct(0);
      },
      onProgress: (u, t) => setPct(Math.round((u / t) * 100)),
      onSuccess: async () => {
        setStatus("Locking down privacy…");
        try {
          const { data } = await api.post("/admin/vimeo/finalize", {
            vimeo_uri: ticket.vimeo_uri,
          });
          setStatus("");
          setUploading(false);
          setPct(100);
          toast.success("Video uploaded to Vimeo");
          onUploaded?.(data.vimeo_video_id, data.embed_url, data.thumbnail_url);
        } catch (err) {
          toast.error(formatApiError(err?.response?.data?.detail) || "Finalize failed");
          setUploading(false);
        }
      },
    });
    uploadRef.current = upload;
    upload.start();
  };

  const cancel = () => {
    try { uploadRef.current?.abort(); } catch {}
    setUploading(false);
    setPct(0);
    setStatus("");
  };

  if (configured === false) {
    return (
      <div className="rounded-lg bg-[#0F0F0F] border border-[#E52E71]/25 p-4 text-sm text-white/70 flex items-start gap-3">
        <AlertTriangle size={16} className="text-[#E52E71] mt-0.5 shrink-0" />
        <div>
          <div className="text-white/85">Vimeo not connected.</div>
          <div className="text-[11px] text-white/50 mt-1">
            Paste <span className="font-mono text-[#FF8A00]">VIMEO_ACCESS_TOKEN</span> in <span className="font-mono">backend/.env</span> and restart to enable direct-to-Vimeo uploads.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={fileRef}
          type="file"
          accept="video/*"
          onChange={(e) => startUpload(e.target.files?.[0])}
          className="hidden"
          data-testid="vimeo-file-input"
        />
        <Button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || configured === null}
          data-testid="vimeo-upload-btn"
          className="rounded-full bg-[#00ADEF] hover:bg-[#0096d1] text-white border-0"
        >
          <Upload size={14} className="mr-2" />
          {uploading ? `Uploading ${pct}%` : "Upload to Vimeo"}
        </Button>
        {value && !uploading && (
          <div className="flex items-center gap-2 text-xs text-[#FF8A00]">
            <CheckCircle size={14} />
            Vimeo ID: <span className="font-mono">{value}</span>
            <button type="button" onClick={onClear}
              className="text-white/40 hover:text-white underline ml-2">clear</button>
          </div>
        )}
        {uploading && (
          <button type="button" onClick={cancel}
            className="text-xs text-white/50 hover:text-white underline">Cancel</button>
        )}
      </div>
      {uploading && (
        <>
          <div className="h-1 rounded-full bg-[#0F0F0F] overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#00ADEF] to-[#FF8A00]"
                 style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[11px] text-white/50 flex items-center gap-1.5">
            <Loader2 size={11} className="animate-spin" /> {status}
          </div>
        </>
      )}
      <div className="text-[11px] text-white/40">
        Direct upload — file streams from your browser to Vimeo. Restricted to your whitelisted domains automatically. Vimeo will finish transcoding a minute or two after upload.
      </div>
    </div>
  );
}
